import { useCallback } from "react";

import { useAirHostReservationSyncStartMutation } from "@/components/features/settings/site-controller/model/air-host/repository/mutation/use-case";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { Button } from "@/components/ui/button/button";

type Props = {
	hotelID: string;
	onRefetchReservationSyncJob: () => Promise<void>;
};

export const SettingsSiteControllerAirHostReservationSyncJobStartSyncSection = ({
	hotelID,
	onRefetchReservationSyncJob,
}: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { onStartReservationSync } = useAirHostReservationSyncStartMutation();

	const handleStartSync = useCallback(async () => {
		const confirmed = window.confirm(
			"既存の予約をAirHostに連携します。この操作は1回のみ実行可能です。続行しますか？",
		);

		if (!confirmed) {
			return;
		}

		showLoadingScreenCallback();

		try {
			const result = await onStartReservationSync({ hotelID });

			const userErrors = result.data?.siteControllerSettingAirHostReservationSyncStart.userErrors ?? [];
			const started = result.data?.siteControllerSettingAirHostReservationSyncStart.started ?? false;

			if (userErrors.length > 0) {
				notifyUserEventInfo({
					variant: "error",
					title: userErrors[0],
				});
				return;
			}

			if (started) {
				notifyUserEventInfo({
					title: "予約同期を開始しました",
				});
				await onRefetchReservationSyncJob();
			}
		} finally {
			hideLoadingScreenCallback();
		}
	}, [
		hotelID,
		onStartReservationSync,
		onRefetchReservationSyncJob,
		showLoadingScreenCallback,
		hideLoadingScreenCallback,
	]);

	return (
		<div className="space-y-4">
			<div className="space-y-2">
				<p>CHILLNNに登録されている予約のうち、操作日を含む操作日以降の予約をAirHostへ連携します。</p>
				<div className="space-y-1 text-sm text-error-600">
					<p>※紐付け設定がされていない部屋を含む予約は、AirHostへ連携されません。</p>
					<p>※この操作は初期連携時の1回のみ実行可能です。紐付け設定が完了してから実行してください。</p>
				</div>
			</div>
			<Button onClick={handleStartSync} variant="outline">
				既存予約をAirHostに連携する
			</Button>
		</div>
	);
};

SettingsSiteControllerAirHostReservationSyncJobStartSyncSection.displayName =
	"SettingsSiteControllerAirHostReservationSyncJobStartSyncSection";
